import { Box, Stack, Tab, Tabs, type Theme, Typography, useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import Alert from '/@/components/bayer/alert';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { updateActiveRecommendationCropConfig } from '/@/store/recommendations/actions';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import type { Field } from '/@/types/field';
import HybridCard, { type Hybrid } from './hybrid-card';
import { useFieldRisks } from './use-field-risks';
import { useHybrids } from './use-hybrids';

type HybridTab = 'recommended' | 'catalog';

/**
 * "Híbridos recomendados" step of the seed recommendation wizard.
 *
 * The list is keyed on the crop config the previous steps built, so it re-ranks whenever the
 * environment changes, and selection is written back to `cropConfig.products` — the shape the
 * recommendation-edit page already saves.
 */
function HybridsStep({ field, crop = 'corn' }: { field: Field; crop?: 'corn' | 'soy' }) {
    const dispatch = useDispatch();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);
    const [tab, setTab] = useState<HybridTab>('recommended');
    const { search } = useLocation();

    const handleChange = (key: string) => {
        return async (value: unknown) => {
            await dispatch(updateActiveRecommendationCropConfig({ key, value }));
        };
    };

    const { featuredProducts, otherProducts, isLoading } = useHybrids({ field, crop });
    const { risks } = useFieldRisks(field);

    // The density-recommendation flow hands off the hybrid the user was already looking at.
    // Seed it once, and only when it's actually among the hybrids offered for this field.
    const preselectedProductId = Number(new URLSearchParams(search).get('product_profile')) || undefined;
    const preselectionApplied = useRef(false);
    const selectedProducts: number[] = cropConfig?.products || [];
    useEffect(() => {
        if (preselectionApplied.current || !preselectedProductId || selectedProducts.length) return;
        const isOffered = [...featuredProducts, ...otherProducts].some(({ id }) => id === preselectedProductId);
        if (!isOffered) return;
        preselectionApplied.current = true;
        dispatch(updateActiveRecommendationCropConfig({ key: 'products', value: [preselectedProductId] }));
    }, [preselectedProductId, selectedProducts.length, featuredProducts, otherProducts, dispatch]);

    if (!cropConfig) {
        return (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        );
    }

    const selectedIds: number[] = cropConfig.products || [];
    const maxSelection = settings.maxProductSelection;
    const atLimit = selectedIds.length >= maxSelection;

    const toggle = (id: number) => {
        const next = selectedIds.includes(id)
            ? selectedIds.filter((selectedId) => selectedId !== id)
            : [...selectedIds, id];
        if (next.length > maxSelection) return;
        handleChange('products')(next);
    };

    const hybrids: Hybrid[] = tab === 'recommended' ? featuredProducts : otherProducts;

    return (
        <Stack spacing={3} pb={4}>
            <Tabs
                value={tab}
                onChange={(_event, value) => setTab(value)}
                variant='fullWidth'
                textColor='secondary'
                indicatorColor='secondary'
            >
                <Tab value='recommended' label={_('Recomendados')} />
                <Tab value='catalog' label={isMobile ? _('Otros híbridos') : _('Otros híbridos del catálogo')} />
            </Tabs>

            {isLoading && !hybrids.length ? (
                <Stack alignItems='center' py={6}>
                    <Spinner />
                </Stack>
            ) : hybrids.length === 0 ? (
                <Alert severity='surfaceVariant'>
                    {_('No encontramos híbridos para este ambiente. Probá ajustando el ambiente del lote.')}
                </Alert>
            ) : (
                <Box
                    display='grid'
                    gap={2}
                    gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
                    alignItems='stretch'
                >
                    {hybrids.map((hybrid) => {
                        const selected = selectedIds.includes(hybrid.id);
                        return (
                            <HybridCard
                                key={hybrid.id}
                                hybrid={hybrid}
                                risks={risks}
                                selected={selected}
                                onToggle={() => toggle(hybrid.id)}
                                disabled={!selected && atLimit}
                                compact={isMobile}
                                showRank={tab === 'recommended' || isMobile}
                                showYield={tab === 'recommended'}
                            />
                        );
                    })}
                </Box>
            )}

            {atLimit && (
                <Typography variant='caption' color='primary.mui.inactive'>
                    {_('Seleccionaste el máximo de {{count}} híbridos. Deseleccioná uno para elegir otro.', {
                        count: maxSelection,
                    })}
                </Typography>
            )}
        </Stack>
    );
}

export default HybridsStep;
